import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { useViewerItemStore } from './useViewerItemStore';

export enum AiMetadataStatus {
  PENDING = 'pending',
  WORKING = 'working',
  DONE = 'done',
  FAILED = 'failed',
}

export interface AiChapters {
  title: string;
  timecode: string;
  startTime: number;
}

export interface AiMetadataStoreState {
  status?: AiMetadataStatus;
  chapters?: AiChapters[];
  provider?: string;
  feedback?: 'positive' | 'negative' | null;
  createdAt?: string;
  updatedAt?: string;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_AI_METADATA);

export const useAiMetadataStore = defineStore('aiMetadataStore', {
  state: () =>
    ({
      chapters: undefined,
      status: undefined,
      provider: undefined,
      feedback: null,
      createdAt: undefined,
      updatedAt: undefined,
    }) as AiMetadataStoreState,
  actions: {
    // Initialize viewer from application store initializer
    initialize(aiMetadata: any) {
      debug('[initialize] Initialize AIMetadata store', aiMetadata);
      if (aiMetadata) {
        this.setAiMetadata(aiMetadata);
      }
    },
    patchAIMetaData(data: any) {
      if (data && data.attributes) {
        this.$patch({
          ...data.attributes,
        });
      }
    },
    setAiMetadata(aiMetadata: any) {
      if (!aiMetadata.data.attributes) {
        debug('Cannot set AI metadata data', aiMetadata);
        return;
      }

      this.$patch({
        ...aiMetadata.data.attributes,
      });
    },
    async useDefaultSummary() {
      const itemStore = useViewerItemStore();

      if (!itemStore.id) {
        return Promise.resolve();
      }

      const res = await Api.item.aiMetadata.patch(itemStore.id);
      if (res && res.data && res.data.item) {
        itemStore.setItem(res.data.item);
      }
    },
    setAiMetadataFeedback(feedback: 'positive' | 'negative' | null) {
      this.$patch({ feedback });
    },
  },
  getters: {
    hasChapters: (state: AiMetadataStoreState) => !!(state.chapters && state.chapters.length > 0),
    isProcessing: (state: AiMetadataStoreState) => state.status === AiMetadataStatus.PENDING || state.status === AiMetadataStatus.WORKING,
  },
});
